import { academyVersionRequest, createVersionedCommandSender } from "../../../shared/academyApi";

export type HandoverSession = {
  exam_session_id: string;
  assessment_title: string;
  cohort_name: string;
  assignment_id: string;
  assignment_version: number;
  assignment_role: string;
};

export type HandoverTeamMember = {
  assignment_id: string;
  proctor_user_id: string;
  proctor_name: string;
  assignment_role: string;
  assignment_version: number;
};

export type ProctorHandover = {
  id: string;
  exam_session_id: string;
  from_proctor_id: string;
  to_proctor_id: string;
  from_proctor_name: string;
  to_proctor_name: string;
  context: {
    roomState: "live_stable" | "technical_monitoring" | "paused_under_policy" | "closing_submission";
    openCandidateSupport: string;
    openIncidentReferences: string;
    requiredNextAction: string;
  };
  state: "pending" | "accepted";
  created_at: string;
  accepted_at: string | null;
  version: number;
  schema_version: "d12-proctor-handover.v1";
  can_accept: boolean;
};

const row = (value: unknown, label: string): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${label} is invalid`);
  return value as Record<string, unknown>;
};
const string = (value: Record<string, unknown>, key: string) => {
  if (typeof value[key] !== "string" || !value[key]) throw new Error(`Handover response is missing ${key}`);
  return value[key] as string;
};
const integer = (value: Record<string, unknown>, key: string) => {
  if (!Number.isInteger(value[key])) throw new Error(`Handover response is missing ${key}`);
  return value[key] as number;
};
const list = <T>(value: unknown, parse: (item: unknown) => T) => {
  if (!Array.isArray(value)) throw new Error("Handover response must be a list");
  return value.map(parse);
};

const parseSession = (value: unknown): HandoverSession => {
  const item = row(value, "Proctor session");
  return {
    exam_session_id: string(item, "exam_session_id"),
    assessment_title: string(item, "assessment_title"),
    cohort_name: string(item, "cohort_name"),
    assignment_id: string(item, "assignment_id"),
    assignment_version: integer(item, "assignment_version"),
    assignment_role: string(item, "assignment_role"),
  };
};

const parseTeamMember = (value: unknown): HandoverTeamMember => {
  const item = row(value, "Proctor team member");
  return {
    assignment_id: string(item, "assignment_id"),
    proctor_user_id: string(item, "proctor_user_id"),
    proctor_name: string(item, "proctor_name"),
    assignment_role: string(item, "assignment_role"),
    assignment_version: integer(item, "assignment_version"),
  };
};

const parseHandover = (value: unknown): ProctorHandover => {
  const item = row(value, "Proctor handover");
  const context = row(item.context, "Proctor handover context");
  const state = string(item, "state");
  const roomState = string(context, "roomState");
  if (!(state === "pending" || state === "accepted")) throw new Error("Handover state is invalid");
  if (
    !(
      ["live_stable", "technical_monitoring", "paused_under_policy", "closing_submission"] as string[]
    ).includes(roomState)
  )
    throw new Error("Handover room state is invalid");
  if (string(item, "schema_version") !== "d12-proctor-handover.v1")
    throw new Error("Handover schema version is unsupported");
  return {
    id: string(item, "id"),
    exam_session_id: string(item, "exam_session_id"),
    from_proctor_id: string(item, "from_proctor_id"),
    to_proctor_id: string(item, "to_proctor_id"),
    from_proctor_name: string(item, "from_proctor_name"),
    to_proctor_name: string(item, "to_proctor_name"),
    context: {
      roomState: roomState as ProctorHandover["context"]["roomState"],
      openCandidateSupport: string(context, "openCandidateSupport"),
      openIncidentReferences: string(context, "openIncidentReferences"),
      requiredNextAction: string(context, "requiredNextAction"),
    },
    state,
    created_at: string(item, "created_at"),
    accepted_at: item.accepted_at === null ? null : string(item, "accepted_at"),
    version: integer(item, "version"),
    schema_version: "d12-proctor-handover.v1",
    can_accept: item.can_accept === true,
  };
};

const initiate = createVersionedCommandSender("d12-proctor-handover");
const accept = createVersionedCommandSender("d12-proctor-handover-acceptance");

export async function listHandoverSessions(signal?: AbortSignal) {
  return list(
    await academyVersionRequest<unknown>(
      "v1/proctor/sessions?limit=200",
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
    parseSession,
  );
}

export async function loadHandoverWorkspace(examSessionId: string, signal?: AbortSignal) {
  const session = encodeURIComponent(examSessionId);
  const [team, handovers] = await Promise.all([
    academyVersionRequest<unknown>(
      `v1/proctor/sessions/${session}/team`,
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
    academyVersionRequest<unknown>(
      `v1/proctor/sessions/${session}/handovers`,
      undefined,
      "GET",
      true,
      undefined,
      undefined,
      signal,
    ),
  ]);
  return { team: list(team, parseTeamMember), handovers: list(handovers, parseHandover) };
}

export function initiateProctorHandover(
  examSessionId: string,
  input: {
    targetAssignmentId: string;
    expectedSourceAssignmentVersion: number;
    expectedTargetAssignmentVersion: number;
    roomState: ProctorHandover["context"]["roomState"];
    openCandidateSupport: string;
    openIncidentReferences: string;
    requiredNextAction: string;
  },
) {
  return initiate<unknown>(`v1/proctor/sessions/${encodeURIComponent(examSessionId)}/handovers`, input);
}

export function acceptProctorHandover(examSessionId: string, handoverId: string, expectedVersion: number) {
  return accept<unknown>(
    `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/handovers/${encodeURIComponent(handoverId)}/accept`,
    { expectedVersion },
  );
}
